import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import translations from './translations';

const LanguageContext = createContext(null);

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English', dir: 'ltr' },
  { code: 'fr', label: 'FR', name: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'عربي', name: 'العربية', dir: 'rtl' },
];

const STORAGE_KEY = 'goldcare_lang';

const getInitialLang = () => {
  if (typeof window === 'undefined') return 'en';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && translations[saved]) return saved;
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return translations[nav] ? nav : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitialLang);

  const setLang = useCallback((code) => {
    if (!translations[code]) return;
    setLangState(code);
    try { window.localStorage.setItem(STORAGE_KEY, code); } catch (err) { /* noop */ }
  }, []);

  // Sync <html> lang + dir for RTL support
  useEffect(() => {
    const meta = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', meta.code);
      document.documentElement.setAttribute('dir', meta.dir);
    }
  }, [lang]);

  const t = useCallback(
    (key, fallback, vars) => {
      const dict = translations[lang] || translations.en;
      let str = dict[key] ?? translations.en[key] ?? fallback ?? key;
      if (vars && typeof str === 'string') {
        Object.keys(vars).forEach((k) => {
          str = str.replaceAll(`{${k}}`, vars[k]);
        });
      }
      return str;
    },
    [lang]
  );

  const value = useMemo(() => {
    const meta = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];
    return { lang, setLang, t, dir: meta.dir, languages: SUPPORTED_LANGUAGES };
  }, [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used inside <LanguageProvider>');
  return ctx;
};

// Convenience helpers for dynamic labels
export const useCategoryLabel = () => {
  const { t } = useLang();
  return (cat) => t(`cat.${cat.id}`, cat.label);
};
export const useTreatmentLabel = () => {
  const { t } = useLang();
  return (tr) => t(`tr.${tr.slug}`, tr.label);
};
